import type {
  ChangeEvent,
  ChangeEventHandler,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { styleVars } from '../lib/style.ts';

type FieldInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'onChange'> & {
  onChange?: ChangeEventHandler<HTMLInputElement>;
};
type FieldSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'>;

export interface FieldLabelProps extends Omit<LabelHTMLAttributes<HTMLLabelElement>, 'className'> {
  children: ReactNode;
}

export function FieldLabel({ children, ...props }: FieldLabelProps) {
  return (
    <label className="fieldLabel" {...props}>
      {children}
    </label>
  );
}

export interface LabeledFieldProps {
  label: ReactNode;
  htmlFor?: string | undefined;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}

/** Labelled field wrapper for composed controls such as copy fields. */
export function LabeledField({ label, htmlFor, hint, error, children }: LabeledFieldProps) {
  return (
    <div className="field">
      {htmlFor ? (
        <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>
      ) : (
        <span className="fieldLabel">{label}</span>
      )}
      {children}
      {hint ? <p className="fieldHint">{hint}</p> : null}
      {error ? (
        <p className="fieldError" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends FieldInputProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  inputClassName?: string;
}

/** Labelled native input matching the design-system field geometry. */
export function TextField({ label, hint, error, id, inputClassName, ...props }: TextFieldProps) {
  return (
    <div className="field">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input id={id} className={['control', inputClassName].filter(Boolean).join(' ')} {...props} />
      {hint ? <p className="fieldHint">{hint}</p> : null}
      {error ? (
        <p className="fieldError" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface AffixedTextFieldProps extends Omit<FieldInputProps, 'prefix'> {
  label: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  inputClassName?: string;
  inputWidth?: string;
}

/** Labelled input paired with compact prefix or suffix content. */
export function AffixedTextField({
  label,
  prefix,
  suffix,
  hint,
  error,
  id,
  inputClassName,
  inputWidth,
  ...props
}: AffixedTextFieldProps) {
  return (
    <LabeledField label={label} htmlFor={id} hint={hint} error={error}>
      <div
        className="affixedField"
        style={styleVars({ '--affixed-field-input-width': inputWidth })}
      >
        {prefix ? <span className="fieldAffix">{prefix}</span> : null}
        <input
          id={id}
          className={['control affixedFieldInput', inputClassName].filter(Boolean).join(' ')}
          {...props}
        />
        {suffix ? <span className="fieldAffix">{suffix}</span> : null}
      </div>
    </LabeledField>
  );
}

export interface SelectFieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends Omit<FieldSelectProps, 'children'> {
  label: ReactNode;
  options: SelectFieldOption[];
  hint?: ReactNode;
  selectClassName?: string;
}

/** Labelled native select styled to match TextField. */
export function SelectField({
  label,
  options,
  hint,
  id,
  selectClassName,
  ...props
}: SelectFieldProps) {
  return (
    <div className="field">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <select id={id} className={['control', selectClassName].filter(Boolean).join(' ')} {...props}>
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? <p className="fieldHint">{hint}</p> : null}
    </div>
  );
}

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type' | 'onChange'
> {
  label: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

/** Native checkbox with the portal accent colour. */
export function Checkbox({ label, ...props }: CheckboxProps) {
  return (
    <label className="checkbox">
      <input type="checkbox" className="checkboxInput" {...props} />
      {label}
    </label>
  );
}
